import type { RouteLocationNormalizedGeneric } from 'vue-router'

/**
 * What the RUM page load plugins share: whether the page is still being
 * prerendered, when the visitor started seeing it, and whether a route change
 * left it.
 */

export const isPrerendering = (): boolean => {
  const doc: Document & { prerendering?: boolean } = document
  return doc.prerendering === true
}

/**
 * Ms from the time origin until a prerendered page was shown, 0 for a normal
 * navigation. Zero until a prerendered page is shown, so read it when the
 * timing is taken and not when a plugin starts.
 */
export const activationStart = (): number => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const navigation = performance.getEntriesByType('navigation')[0] as
    | (PerformanceNavigationTiming & { activationStart?: number })
    | undefined
  return navigation?.activationStart ?? 0
}

/** Epoch ms the visitor started seeing the page. */
export const pageStart = (): number =>
  Math.round(performance.timeOrigin + activationStart())

const withoutTrailingSlash = (path: string) => path.replace(/\/+$/, '') || '/'

/**
 * Whether a route change left the landing page. A query change (a category
 * tab) or a trailing slash is the same page, another path is not. The first
 * navigation, from no matched route, is not a change.
 */
export const isAnotherPage = (
  to: Pick<RouteLocationNormalizedGeneric, 'path'>,
  from: Pick<RouteLocationNormalizedGeneric, 'path' | 'matched'>
): boolean =>
  from.matched.length > 0 &&
  withoutTrailingSlash(to.path) !== withoutTrailingSlash(from.path)
